import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import Button from '@jetbrains/ring-ui/components/button/button';
import refreshIcon from '@jetbrains/icons/update';
import { isEqual } from 'lodash';
import okIcon from '@jetbrains/icons/ok';
import activityIcon from '@jetbrains/icons/activity';
import failedIcon from '@jetbrains/icons/exception-empty';
import { AI_PROVIDERS } from '@modules/ai/common/constants';
import { assignTestId } from '@common/infra/tests/assign-test-id';
import iconLoaders from '@common/loaders/icon-loaders.css';
import { useTestProviderConnection } from '@common/queries/ai-enterprise';
import { PopupMessage } from '@common/popup-message/popup-message';
import { ProviderTestConnectionResult } from './test-connection-dialog/provider-test-connection-result';
import styles from './provider-test-connection-button.css';
export function ProviderTestConnectionButton({ provider, disabled, ...rest }) {
    var _a, _b;
    const [lastTestedProviderState, setLastTestedProviderState] = useState();
    const popupRef = useRef(null);
    const providerTestMutation = useTestProviderConnection();
    const testConnection = useCallback(() => {
        providerTestMutation.mutate(provider, {
            onSuccess: result => {
                var _a;
                setLastTestedProviderState({ ...provider });
                if (result.status === 'Error') {
                    (_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.show();
                }
            }
        });
    }, [provider, providerTestMutation]);
    useEffect(() => {
        if (providerTestMutation.data && !isEqual(lastTestedProviderState, provider)) {
            providerTestMutation.reset();
        }
    }, [lastTestedProviderState, provider, providerTestMutation, providerTestMutation.data]);
    const testConnectionIcon = useMemo(() => {
        var _a, _b;
        if (((_a = providerTestMutation.data) === null || _a === void 0 ? void 0 : _a.status) === 'Success') {
            return okIcon;
        }
        if (((_b = providerTestMutation.data) === null || _b === void 0 ? void 0 : _b.status) === 'Error') {
            return failedIcon;
        }
        if (providerTestMutation.isLoading) {
            return refreshIcon;
        }
        return activityIcon;
    }, [(_a = providerTestMutation.data) === null || _a === void 0 ? void 0 : _a.status, providerTestMutation.isLoading]);
    const button = useMemo(() => {
        var _a, _b, _c, _d;
        return (_jsx(Button, { inline: true, onClick: testConnection, loader: providerTestMutation.isLoading, disabled: providerTestMutation.isLoading || disabled, className: classNames({
                [styles.button]: true,
                [iconLoaders.refreshIconLoader]: providerTestMutation.isLoading,
                [styles.iconActive]: !providerTestMutation.data && !disabled,
                [styles.iconSuccess]: ((_a = providerTestMutation.data) === null || _a === void 0 ? void 0 : _a.status) === 'Success',
                [styles.iconError]: ((_b = providerTestMutation.data) === null || _b === void 0 ? void 0 : _b.status) === 'Error'
            }), icon: testConnectionIcon, ...assignTestId('provider-test-connection-button', {
                success: ((_c = providerTestMutation.data) === null || _c === void 0 ? void 0 : _c.status) === 'Success',
                error: ((_d = providerTestMutation.data) === null || _d === void 0 ? void 0 : _d.status) === 'Error'
            }, rest), children: "Test connection" }));
    }, [
        testConnection,
        providerTestMutation.isLoading,
        providerTestMutation.data,
        disabled,
        testConnectionIcon,
        rest
    ]);
    return (_jsx(PopupMessage, { title: `${((_b = AI_PROVIDERS[provider.id]) === null || _b === void 0 ? void 0 : _b.label) || ''} Test Connection`, anchor: button, ref: popupRef, children: providerTestMutation.data && (_jsx(ProviderTestConnectionResult, { testResult: providerTestMutation.data })) }));
}
